import type { User } from 'oidc-client-ts';

const ORGS_CLAIM = 'https://sirk.app/organizations';
const PERMISSIONS_CLAIM = 'https://sirk.app/permissions';

/**
 * Decode a JWT payload (no signature verification — the token was already
 * obtained over TLS from the OIDC provider). Returns {} on any malformed input.
 *
 * Sirk carries its per-org claims in the ACCESS token, not the ID token, so the
 * org/permission claims must be read from `user.access_token` rather than
 * `user.profile`.
 */
export function decodeJwtPayload(token: string | undefined): Record<string, unknown> {
  if (!token) return {};
  try {
    const segment = token.split('.')[1];
    if (!segment) return {};
    // base64url → base64, then re-pad to a multiple of 4: JWT payloads drop the
    // trailing '=', which atob rejects in stricter (non-browser) runtimes.
    const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, '=');
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/**
 * Every org segment the user may act within according to the JWT — the union of
 * the `https://sirk.app/organizations` claim and the org prefixes of the
 * `https://sirk.app/permissions` claim (`<orgSegment>:<verb>:<resource>`).
 *
 * This is deliberately broader than the member-scoped
 * `GET /?permission=READ_ORGANIZATION` listing: cross-tenant *granted* orgs
 * appear here (the grant injects them into both claims) but never in that
 * listing, because the user holds no membership row for them. Use this — not the
 * fetched member list — as the authority for whether the user may select a given
 * org, so a `?org=<granted-org>` deep-link isn't discarded.
 *
 * These claims drive client-side segment routing/UX only; the backend
 * re-validates the token on every request, so an unverified decode is safe here.
 */
export function accessibleOrgIds(user: User | null | undefined): string[] {
  if (!user) return [];
  const access = decodeJwtPayload(user.access_token);
  // Fall back to profile (ID token / userinfo) for providers that surface the
  // claims there instead of in the access token.
  const profile = (user.profile ?? {}) as Record<string, unknown>;
  const ids = new Set<string>();

  const orgsClaim = access[ORGS_CLAIM] ?? profile[ORGS_CLAIM];
  if (Array.isArray(orgsClaim)) {
    for (const id of orgsClaim) {
      if (typeof id === 'string' && id) ids.add(id);
    }
  }

  // Also derive segments from the permission strings — robust even if the
  // `organizations` claim is ever absent, since access is permission-gated.
  // Trust the prefix only on a full `<org>:<verb>:<resource>` triple, so a
  // stray colonless value can't masquerade as an org segment.
  const permsClaim = access[PERMISSIONS_CLAIM] ?? profile[PERMISSIONS_CLAIM];
  if (Array.isArray(permsClaim)) {
    for (const perm of permsClaim) {
      if (typeof perm === 'string') {
        const parts = perm.split(':');
        if (parts.length >= 3 && parts[0]) ids.add(parts[0]);
      }
    }
  }

  return [...ids];
}

/**
 * The raw `<org>:<verb>:<resource>` permission strings the token carries, from the access
 * token with a profile fallback — the same two places {@link accessibleOrgIds} looks.
 *
 * Client-side gating only. Every endpoint re-derives these from the token it receives, so
 * this decides what to render and never what is allowed.
 */
export function readPermissionsFromUser(user: User | null | undefined): string[] {
  if (!user) return [];
  const access = decodeJwtPayload(user.access_token);
  const profile = (user.profile ?? {}) as Record<string, unknown>;
  const claim = access[PERMISSIONS_CLAIM] ?? profile[PERMISSIONS_CLAIM];
  return Array.isArray(claim) ? claim.filter((p): p is string => typeof p === 'string') : [];
}
